import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { jsPDF } from "jspdf";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

const LIMIT = 12;

const translateBackend = (t, data) => {
  if (!data) return t("msg_server_or_network");
  if (data.message_key) return t(data.message_key, data.message_params || {});
  return t("msg_unknown_error");
};

const QrGenerator = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [count, setCount] = useState(1);
  const [tab, setTab] = useState("generate");

  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageGen, setPageGen] = useState(0);
  const [qrSize, setQrSize] = useState(3);

  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [pageHist, setPageHist] = useState(0);

  const [errorMsg, setErrorMsg] = useState("");
  const [limitDialog, setLimitDialog] = useState(null);
  const [application, setApplication] = useState("");

  useEffect(() => {
    const storedApp = localStorage.getItem("userApplication") || "";
    setApplication(storedApp);
  }, []);

  const generateQR = async () => {
    const numCount = parseInt(count, 10);
    if (isNaN(numCount) || numCount < 1) {
      setErrorMsg(t("qr_err_invalid_number"));
      return;
    }
    if (!String(application || "").trim()) {
      setErrorMsg(t("qr_app_missing"));
      return;
    }

    setErrorMsg("");
    setLoading(true);
    setResults([]);
    setPageGen(0);

    try {
      const res = await axios.post(`${process.env.REACT_APP_API_URL}/api/generate_qr`, {
        count: numCount,
        application,
        size: qrSize,
      });

      setResults(res.data?.data || []);
      setTab("generate");
    } catch (err) {
      const errorData = err?.response?.data;
      const translatedMessage = translateBackend(t, errorData);
      setErrorMsg(translatedMessage);

      if (
        err?.response?.status === 403 &&
        (errorData?.subscription_required ||
          ["qr_limit_reached", "qr_limit_exceeded"].includes(errorData?.message_key))
      ) {
        setLimitDialog({
          message: translatedMessage,
          limit: errorData?.limit,
          used: errorData?.used,
          remaining: errorData?.remaining,
          pricingPath: errorData?.pricing_path || "/pricing",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const closeLimitDialog = () => setLimitDialog(null);

  const goToPricing = () => {
    const pricingPath = limitDialog?.pricingPath || "/pricing";
    setLimitDialog(null);
    navigate(pricingPath);
  };

  const deleteQRCode = async (qrCode) => {
    if (!window.confirm(t("qr_confirm_delete"))) return;

    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/qr/${qrCode}`);
      setHistory((prev) => prev.filter((qr) => qr.code !== qrCode));
    } catch (err) {
      setErrorMsg(translateBackend(t, err?.response?.data));
    }
  };

  const loadHistory = useCallback(async ({
    openHistoryTab = false,
    showLoading = true,
    showErrors = true,
    resetPage = true,
  } = {}) => {
    if (!String(application || "").trim()) {
      if (showErrors) setErrorMsg(t("qr_app_missing"));
      return;
    }

    if (showErrors) setErrorMsg("");
    if (showLoading) setLoadingHistory(true);
    if (resetPage) setPageHist(0);

    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/qr_history`, {
        params: { application },
      });

      setHistory(res.data?.data || []);
      if (openHistoryTab) setTab("history");
    } catch (err) {
      if (showErrors) setErrorMsg(translateBackend(t, err?.response?.data));
    } finally {
      if (showLoading) setLoadingHistory(false);
    }
  }, [application, t]);

  const fetchHistory = useCallback(async () => {
    await loadHistory({
      openHistoryTab: true,
      showLoading: true,
      showErrors: true,
      resetPage: true,
    });
  }, [loadHistory]);

  useEffect(() => {
    if (!String(application || "").trim()) return;
    loadHistory({
      openHistoryTab: false,
      showLoading: false,
      showErrors: false,
      resetPage: false,
    });
  }, [application, loadHistory]);

  const printQRCode = (qr) => {
    const win = window.open("", "_blank");
    if (!win) {
      setErrorMsg(t("rp_popup_blocked", "Popup blocked. Please allow popups."));
      return;
    }

    const label = t("assist_by_scan");

    win.document.write(`
      <html>
        <head>
          <meta charset="utf-8" />
          <title>${t("qr_print_title")}</title>
          <style>
            @page { margin: 0; size: auto; }
            body {
              display: flex; justify-content: center; align-items: center;
              height: 100vh; margin: 0; flex-direction: column;
            }
            img {
              width: ${qr.size_cm ?? qrSize}cm;
              height: ${qr.size_cm ?? qrSize}cm;
              image-rendering: pixelated;
            }
            div {
              margin-top: 10px;
              font-style: italic;
              font-family: Arial, sans-serif;
            }
          </style>
        </head>
        <body>
          <img src="${process.env.REACT_APP_API_URL}${qr.image_path}" alt="QR code" />
          <div>${label}</div>
          <script>
            window.onload = () => { window.print(); };
          </script>
        </body>
      </html>
    `);

    win.document.close();
  };

  const totalGen = results.length;
  const totalPagesGen = Math.max(1, Math.ceil(totalGen / LIMIT));
  const pageGenClamped = Math.min(pageGen, totalPagesGen - 1);
  const genStart = pageGenClamped * LIMIT;
  const genEnd = Math.min(totalGen, genStart + LIMIT);
  const pageResults = results.slice(genStart, genEnd);

  const totalHist = history.length;
  const totalPagesHist = Math.max(1, Math.ceil(totalHist / LIMIT));
  const pageHistClamped = Math.min(pageHist, totalPagesHist - 1);
  const histStart = pageHistClamped * LIMIT;
  const histEnd = Math.min(totalHist, histStart + LIMIT);
  const pageHistory = history.slice(histStart, histEnd);

  const cmToMm = (cm) => cm * 10;

  const loadImageAsDataURL = async (url) => {
    const resp = await fetch(url, { mode: "cors" });
    if (!resp.ok) throw new Error("Failed to load image: " + url);

    const blob = await resp.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = reject;
      reader.onload = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });
  };

  const downloadSingleQrPdf = async (qr) => {
    try {
      const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });

      const pageW = pdf.internal.pageSize.getWidth();
      const sizeCm = qr.size_cm ?? qrSize;
      const qrMm = cmToMm(sizeCm);

      const imgUrl = `${process.env.REACT_APP_API_URL}${qr.image_path}?v=${qr.code}`;
      const dataUrl = await loadImageAsDataURL(imgUrl);

      const x = (pageW - qrMm) / 2;
      const y = 45;

      pdf.addImage(dataUrl, "PNG", x, y, qrMm, qrMm);

      pdf.setFont("helvetica", "italic");
      pdf.setFontSize(14);
      pdf.text(t("assist_by_scan"), pageW / 2, y + qrMm + 12, { align: "center" });

      pdf.save(`${qr.code}.pdf`);
    } catch (e) {
      console.error(e);
      setErrorMsg(t("qr_err_pdf_single"));
    }
  };

  const exportQrsToA4Pdf = async (qrList, sizeCm) => {
    if (!qrList?.length) return;

    setErrorMsg("");
    try {
      const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });

      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();

      const margin = 10;
      const gap = 4;
      const qrMm = cmToMm(sizeCm);

      const label = t("assist_by_scan");
      const fontSize = sizeCm <= 1.5 ? 7 : 9;
      const lineHeight = 3.2;
      const labelTopGap = 2.5;
      const labelMaxWidth = qrMm;

      pdf.setFont("helvetica", "italic");
      pdf.setFontSize(fontSize);
      const labelLinesExample = pdf.splitTextToSize(label, labelMaxWidth);
      const labelBlockH = labelLinesExample.length * lineHeight;

      const cellH = qrMm + labelTopGap + labelBlockH + gap;
      const cellW = qrMm + gap;

      const usableW = pageW - margin * 2;
      const usableH = pageH - margin * 2;

      const cols = Math.max(1, Math.floor((usableW + gap) / cellW));
      const rows = Math.max(1, Math.floor((usableH + gap) / cellH));
      const perPage = cols * rows;

      const gridW = cols * qrMm + (cols - 1) * gap;
      const gridH = rows * (qrMm + labelTopGap + labelBlockH) + (rows - 1) * gap;

      const startX = margin + (usableW - gridW) / 2;
      const startY = margin + (usableH - gridH) / 2;

      for (let i = 0; i < qrList.length; i++) {
        const indexInPage = i % perPage;
        if (i > 0 && indexInPage === 0) pdf.addPage();

        const r = Math.floor(indexInPage / cols);
        const c = indexInPage % cols;

        const x = startX + c * (qrMm + gap);
        const y = startY + r * (qrMm + labelTopGap + labelBlockH + gap);

        const imgUrl = `${process.env.REACT_APP_API_URL}${qrList[i].image_path}?v=${qrList[i].code}`;
        const dataUrl = await loadImageAsDataURL(imgUrl);

        pdf.addImage(dataUrl, "PNG", x, y, qrMm, qrMm);

        pdf.setFont("helvetica", "italic");
        pdf.setFontSize(fontSize);

        const lines = pdf.splitTextToSize(label, labelMaxWidth);
        const textY = y + qrMm + labelTopGap + lineHeight;
        pdf.text(lines, x + qrMm / 2, textY, { align: "center" });
      }

      const filename = `QR_${application || "app"}_${sizeCm}cm_${Date.now()}.pdf`;
      pdf.save(filename);
    } catch (e) {
      console.error(e);
      setErrorMsg(t("qr_err_pdf_export"));
    }
  };

  const fieldClass =
    "w-full rounded-lg border border-input bg-background px-3 py-2.5 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-ring/20";
  const primaryBtnClass =
    "inline-flex items-center justify-center rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground transition-all hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-60";
  const secondaryBtnClass =
    "inline-flex items-center justify-center rounded-lg border border-border px-3 py-2 text-sm font-medium text-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50";
  const errorMsgClass = "rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive";
  const infoMsgClass = "rounded-lg border border-border bg-muted/60 px-4 py-3 text-sm text-muted-foreground";
  const getQrUsername = (qr) => qr?.username || qr?.user_name || qr?.user || "—";

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      <h1 className="text-2xl font-bold text-foreground">{t("qr_title")}</h1>

      <div className="mt-6 flex gap-1 rounded-lg bg-muted p-1">
        <button
          onClick={() => setTab("generate")}
          className={`flex-1 rounded-md px-4 py-2 text-sm font-medium transition-colors ${
            tab === "generate" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground"
          }`}
        >
          {t("qr_tab_generate")}
        </button>
        <button
          onClick={fetchHistory}
          disabled={loadingHistory}
          className={`flex-1 rounded-md px-4 py-2 text-sm font-medium transition-colors ${
            tab === "history" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground"
          }`}
        >
          {loadingHistory ? t("loading") : `${t("qr_tab_history")} (${totalHist})`}
        </button>
      </div>

      {tab === "generate" ? (
        <div className="mt-6">
          <div className="rounded-xl border border-border bg-card p-6">
            <div className="grid gap-4 sm:grid-cols-3">
              <div>
                <label className="mb-1 block text-sm font-medium text-foreground">{t("qr_count_aria", "Nombre")}</label>
                <input
                  type="number"
                  min={1}
                  max={50}
                  value={count}
                  onChange={(e) => setCount(e.target.value)}
                  className={fieldClass}
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-foreground">{t("qr_size_aria", "Taille")}</label>
                <select value={qrSize} onChange={(e) => setQrSize(parseFloat(e.target.value))} className={fieldClass}>
                  <option value={1.5}>{t("qr_size_option", { size: 1.5 })}</option>
                  <option value={3}>{t("qr_size_option", { size: 3 })}</option>
                  <option value={4.5}>{t("qr_size_option", { size: 4.5 })}</option>
                  <option value={6}>{t("qr_size_option", { size: 6 })}</option>
                </select>
              </div>
              <div className="flex items-end">
                <button onClick={generateQR} disabled={loading} className={`${primaryBtnClass} w-full`}>
                  {loading ? t("qr_generating") : t("generate", "Générer")}
                </button>
              </div>
            </div>
          </div>

          {errorMsg && <p className={`mt-4 ${errorMsgClass}`}>{errorMsg}</p>}
          {loading && <p className={`mt-4 ${infoMsgClass}`}>{t("qr_generation_progress")}</p>}

          {results.length > 0 && (
            <div className="mt-6">
              <div className="mb-4 flex items-center justify-between">
                <h2 className="text-lg font-semibold text-foreground">{t("qr_generated_title", "QR codes générés")}</h2>
                <button onClick={() => exportQrsToA4Pdf(results, qrSize)} className={secondaryBtnClass}>
                  {t("qr_download_a4_pdf", "Télécharger tous")}
                </button>
              </div>

              <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3">
                {pageResults.map((qr) => (
                  <div key={qr.code} className="flex min-h-[320px] flex-col rounded-xl border border-border bg-card p-4 text-center">
                    <p className="truncate text-sm font-medium text-foreground">{(qr.image_path || "").split("/").pop()}</p>
                    <p className="mt-1 text-xs text-muted-foreground">{t("username", "Utilisateur")}: {getQrUsername(qr)}</p>
                    <div className="mx-auto mt-3 flex h-48 w-full items-center justify-center rounded-lg bg-muted p-2">
                      <img
                        src={`${process.env.REACT_APP_API_URL}${qr.image_path}?v=${qr.code}`}
                        alt={t("qr_alt", { code: qr.code })}
                        className="img-fade h-40 w-40 object-contain"
                        onLoad={(e) => e.currentTarget.classList.add("loaded")}
                      />
                    </div>
                    <button onClick={() => downloadSingleQrPdf(qr)} className="mt-auto pt-3 text-xs text-primary hover:underline">
                      {t("qr_download_pdf", "Télécharger")}
                    </button>
                  </div>
                ))}
              </div>

              {totalPagesGen > 1 && (
                <div className="mt-4 flex justify-center gap-2">
                  <button onClick={() => setPageGen((p) => Math.max(0, p - 1))} disabled={pageGenClamped === 0} className={secondaryBtnClass}>
                    {t("prev", "Précédent")}
                  </button>
                  <span className="flex items-center px-3 text-sm text-muted-foreground">{pageGenClamped + 1} / {totalPagesGen}</span>
                  <button onClick={() => setPageGen((p) => Math.min(totalPagesGen - 1, p + 1))} disabled={pageGenClamped >= totalPagesGen - 1} className={secondaryBtnClass}>
                    {t("next", "Suivant")}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="mt-6">
          {errorMsg && <p className={`mb-4 ${errorMsgClass}`}>{errorMsg}</p>}
          {loadingHistory && <p className={`mb-4 ${infoMsgClass}`}>{t("qr_loading_history")}</p>}

          <div className="overflow-hidden rounded-xl border border-border bg-card">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/50">
                  <th className="px-4 py-3 text-start font-medium text-muted-foreground">Code</th>
                  <th className="px-4 py-3 text-start font-medium text-muted-foreground">QR</th>
                  <th className="px-4 py-3 text-start font-medium text-muted-foreground">{t("status", "Statut")}</th>
                  <th className="px-4 py-3 text-start font-medium text-muted-foreground">{t("username", "Utilisateur")}</th>
                  <th className="px-4 py-3 text-start font-medium text-muted-foreground">{t("actions", "Actions")}</th>
                </tr>
              </thead>
              <tbody>
                {pageHistory.map((qr) => (
                  <tr key={qr.code} className="border-b border-border last:border-0">
                    <td className="px-4 py-3 font-medium text-foreground">{qr.code}</td>
                    <td className="px-4 py-3">
                      {qr.image_path ? (
                        <img src={`${process.env.REACT_APP_API_URL}${qr.image_path}`} alt={t("qr_alt", { code: qr.code })} width={40} height={40} />
                      ) : null}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${qr.status === "active" ? "bg-success/10 text-success" : "bg-muted text-muted-foreground"}`}>
                        {qr.status === "active" ? t("qr_status_active", "Actif") : t("qr_status_not_active", "Inactif")}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{getQrUsername(qr)}</td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <button onClick={() => printQRCode(qr)} className="text-xs text-primary hover:underline">{t("print", "Imprimer")}</button>
                        <button onClick={() => deleteQRCode(qr.code)} className="text-xs text-destructive hover:underline">{t("delete", "Supprimer")}</button>
                      </div>
                    </td>
                  </tr>
                ))}
                {!loadingHistory && pageHistory.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-6 text-center text-sm text-muted-foreground">
                      {t("qr_no_history")}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {totalPagesHist > 1 && (
            <div className="mt-4 flex justify-center gap-2">
              <button onClick={() => setPageHist((p) => Math.max(0, p - 1))} disabled={pageHistClamped === 0} className={secondaryBtnClass}>
                {t("prev", "Précédent")}
              </button>
              <span className="flex items-center px-3 text-sm text-muted-foreground">{pageHistClamped + 1} / {totalPagesHist}</span>
              <button onClick={() => setPageHist((p) => Math.min(totalPagesHist - 1, p + 1))} disabled={pageHistClamped >= totalPagesHist - 1} className={secondaryBtnClass}>
                {t("next", "Suivant")}
              </button>
            </div>
          )}
        </div>
      )}

      {limitDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/50 p-4">
          <div className="w-full max-w-sm rounded-2xl border border-border bg-card p-6 text-center shadow-2xl">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-primary"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
            </div>
            <h3 className="mt-4 text-lg font-bold text-foreground">{t("qr_limit_dialog_title")}</h3>
            <p className="mt-2 text-sm text-muted-foreground">{limitDialog.message}</p>
            <div className="mt-6 flex gap-3">
              <button onClick={closeLimitDialog} className="flex-1 rounded-lg border border-border px-4 py-2.5 text-sm font-medium text-muted-foreground">
                {t("close", "Fermer")}
              </button>
              <button onClick={goToPricing} className="flex-1 rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground">
                {t("qr_limit_dialog_subscribe", "Upgrade")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QrGenerator;
